import apiClient from './client';
import {
  MonthlySummary,
  DailySummary,
  DriverStats,
  AllDriversSummary,
  ReportFilters,
  YearlySummary,
  YearlyDrivers,
  YearlyVehicles,
} from '../types/report';

export const reportsApi = {
  getMonthlySummary: async (
    month: string,
    driverId?: string
  ): Promise<{ success: boolean; data: MonthlySummary }> => {
    const params = new URLSearchParams({ month });
    if (driverId) params.append('driverId', driverId);

    const response = await apiClient.get(`/api/reports/monthly-summary?${params.toString()}`);
    return response.data;
  },

  getDriverStats: async (
    startDate: string,
    endDate: string,
    driverId?: string
  ): Promise<{ success: boolean; data: DriverStats }> => {
    const params = new URLSearchParams({ startDate, endDate });
    if (driverId) params.append('driverId', driverId);

    const response = await apiClient.get(`/api/reports/stats?${params.toString()}`);
    return response.data;
  },

  getDailySummary: async (
    startDate: string,
    endDate: string,
    driverId?: string
  ): Promise<{ success: boolean; data: DailySummary[] }> => {
    const params = new URLSearchParams({ startDate, endDate });
    if (driverId) params.append('driverId', driverId);

    const response = await apiClient.get(`/api/reports/daily?${params.toString()}`);
    return response.data;
  },

  getAllDriversSummary: async (
    startDate: string,
    endDate: string
  ): Promise<{ success: boolean; data: AllDriversSummary }> => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await apiClient.get(`/api/reports/all-drivers?${params.toString()}`);
    return response.data;
  },

  exportExcel: async (filters: ReportFilters): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    // filters.month is "yyyy-MM" — backend expects just the numeric month
    if (filters.month) {
      const parts = filters.month.split('-');
      params.append('month', parts[1] ?? parts[0]);
      if (!filters.year) params.append('year', parts[0]);
    }
    if (filters.year != null) params.append('year', String(filters.year));
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.driverId) params.append('driverId', filters.driverId);
    if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
    if (filters.qualification) params.append('qualification', filters.qualification);

    const response = await apiClient.get(`/api/reports/export/excel?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getYearlySummary: async (year: number): Promise<{ success: boolean; data: YearlySummary }> => {
    const response = await apiClient.get(`/api/reports/yearly/summary?year=${year}`);
    return response.data;
  },

  getYearlyDrivers: async (year: number): Promise<{ success: boolean; data: YearlyDrivers }> => {
    const response = await apiClient.get(`/api/reports/yearly/drivers?year=${year}`);
    return response.data;
  },

  getYearlyVehicles: async (year: number): Promise<{ success: boolean; data: YearlyVehicles }> => {
    const response = await apiClient.get(`/api/reports/yearly/vehicles?year=${year}`);
    return response.data;
  },

  exportCSV: async (filters: ReportFilters): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    // filters.month is "yyyy-MM" — backend expects just the numeric month
    if (filters.month) {
      const parts = filters.month.split('-');
      params.append('month', parts[1] ?? parts[0]);
      if (!filters.year) params.append('year', parts[0]);
    }
    if (filters.year != null) params.append('year', String(filters.year));
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.driverId) params.append('driverId', filters.driverId);
    if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
    if (filters.qualification) params.append('qualification', filters.qualification);

    const response = await apiClient.get(`/api/reports/export/csv?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
